import LogoutIcon from "@mui/icons-material/Logout"
import MenuIcon from "@mui/icons-material/Menu"
import React, { useState } from "react"
import { Box, AppBar, Toolbar, IconButton, Menu, MenuItem, Button, useMediaQuery, useTheme, Typography } from "@mui/material"
import { useLocation, useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { RootState } from "../app/store"
import { logout, setLanguage } from "../state/user/userSlice"
import { useTranslation } from "react-i18next"
import { LanguageSelector } from "../features/common/LanguageSelector"
import { LanguageCode } from "../features/common/Language"
import { HEADER_HEIGHT_DESKTOP, HEADER_HEIGHT_MOBILE } from "./layoutConstants"
import { uiTokens } from "../common/uiTokens"

const CONCEPT_TEXT_COLOR = '#F8F3EC'

export function Header() {
    const { t } = useTranslation()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
    const navigate = useNavigate()
    const dispatch = useDispatch<any>()
    const { pathname, hash } = useLocation()
    const pathParts = pathname.split('/')
    const userId = pathParts[2]
    const user = useSelector((state: RootState) => state.user)

    const [selectedLanguage, setSelectedLanguage] = useState(user.language)
    const [mobileMenuAnchorEl, setMobileMenuAnchorEl] = useState<null | HTMLElement>(null)
    const openMobileMenu = Boolean(mobileMenuAnchorEl)
    const homePath = `/user/${userId}/home`
    const logPath = `${homePath}#log`

    const activeNavItem =
        pathname === homePath && hash === '#log'
            ? 'log'
            : pathname === homePath || pathname.startsWith(`${homePath}/`)
                ? 'dashboard'
                : pathname.startsWith(`/user/${userId}/meals`)
                    ? 'meals'
                    : pathname.startsWith(`/user/${userId}/tips`)
                        ? 'tips'
                        : pathname.startsWith(`/user/${userId}/feedback`)
                            ? 'feedback'
                            : pathname.startsWith(`/user/${userId}/bio`)
                                ? 'settings'
                                : ''

    const handleLogClick = () => {
        if (pathname.includes(homePath)) {
            document.getElementById('log')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            navigate(logPath, { replace: true })
            return
        }
        navigate(logPath)
    }

    const navItems = [
        { key: 'dashboard', label: t('navigation.dashboard'), action: () => navigate(homePath) },
        { key: 'log', label: t('navigation.log'), action: handleLogClick },
        { key: 'meals', label: t('navigation.history'), action: () => navigate(`/user/${userId}/meals`) },
        { key: 'tips', label: t('navigation.help_tips'), action: () => navigate(`/user/${userId}/tips`) },
        { key: 'feedback', label: t('navigation.feedback'), action: () => navigate(`/user/${userId}/feedback`) },
        { key: 'settings', label: t('navigation.settings'), action: () => navigate(`/user/${userId}/bio`) },
    ]

    const mobileMenuItems = [...navItems]
    const headerHeight = isMobile ? HEADER_HEIGHT_MOBILE : HEADER_HEIGHT_DESKTOP

    const wordmark = (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.1,
                mr: isMobile ? 0.4 : 1.6,
                ml: isMobile ? 0.2 : 0,
                py: isMobile ? 0.1 : 0,
                whiteSpace: 'nowrap',
            }}
            aria-label="Koncept Diet brand"
        >
            <Typography
                sx={{
                    fontFamily: "'Inter', 'Poppins', 'Segoe UI', sans-serif",
                    fontWeight: 800,
                    letterSpacing: '0.15em',
                    color: CONCEPT_TEXT_COLOR,
                    lineHeight: 1,
                    fontSize: isMobile ? '1.08rem' : '1.22rem',
                    textTransform: 'uppercase',
                }}
            >
                KONCEPT
            </Typography>
            <Box
                sx={{
                    width: 1,
                    height: isMobile ? 16 : 20,
                    backgroundColor: 'rgba(241, 232, 220, 0.3)',
                    borderRadius: 1,
                    opacity: 0.75,
                }}
            />
            <Typography
                variant="caption"
                sx={{
                    fontWeight: 600,
                    color: uiTokens.colors.brandModule,
                    textTransform: 'uppercase',
                    fontSize: isMobile ? '0.67rem' : '0.72rem',
                    letterSpacing: '0.12em',
                    lineHeight: 1,
                    pt: 0.2,
                }}
            >
                Diet
            </Typography>
        </Box>
    )

    return (
        <Box>
            {user.user != null && (
                <>
                    <AppBar
                        position="fixed"
                        sx={{
                            backgroundColor: '#312015',
                            top: 0,
                            left: 0,
                            right: 0,
                            paddingTop: 'env(safe-area-inset-top)',
                            boxSizing: 'border-box',
                            boxShadow: '0 10px 24px rgba(16, 10, 6, 0.22)',
                        }}
                    >
                        <Toolbar
                            sx={{
                                gap: isMobile ? 1 : 1.4,
                                width: '100%',
                                minHeight: headerHeight,
                                px: isMobile ? 1 : 2,
                            }}
                        >
                            {isMobile ? (
                                <>
                                    <IconButton
                                        id="mobile-menu-button"
                                        size="large"
                                        edge="start"
                                        color="inherit"
                                        aria-label="menu"
                                        aria-controls={openMobileMenu ? 'mobile-menu' : undefined}
                                        aria-haspopup="true"
                                        aria-expanded={openMobileMenu ? 'true' : undefined}
                                        onClick={(event) => setMobileMenuAnchorEl(event.currentTarget)}
                                        sx={{
                                            minHeight: 44,
                                            minWidth: 44,
                                            borderRadius: 2,
                                        }}
                                    >
                                        <MenuIcon />
                                    </IconButton>
                                    {wordmark}
                                    <Box sx={{ flexGrow: 1 }} />
                                    <LanguageSelector
                                        languages={[LanguageCode.CS, LanguageCode.SK, LanguageCode.EN]}
                                        selectedLanguage={selectedLanguage}
                                        onLanguageChange={(lang) => {
                                            setSelectedLanguage(lang)
                                            dispatch(setLanguage(lang))
                                        }}
                                    />
                                    <IconButton
                                        size="large"
                                        color="inherit"
                                        onClick={() => {
                                            dispatch(logout())
                                            navigate('/')
                                        }}
                                        sx={{
                                            minHeight: 44,
                                            minWidth: 44,
                                            borderRadius: 2,
                                        }}
                                    >
                                        <LogoutIcon />
                                    </IconButton>
                                </>
                            ) : (
                                <>
                                    {wordmark}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, ml: 0.5, mr: 1.2 }}>
                                        {navItems.map((item) => {
                                            const isActive = activeNavItem === item.key

                                            return (
                                                <Button
                                                    key={item.label}
                                                    color="inherit"
                                                    onClick={item.action}
                                                    sx={{
                                                        minHeight: 38,
                                                        px: 1.55,
                                                        py: 0.65,
                                                        whiteSpace: 'nowrap',
                                                        borderRadius: 6,
                                                        textTransform: 'none',
                                                        letterSpacing: '0.01em',
                                                        fontWeight: isActive ? 700 : 500,
                                                        color: isActive ? CONCEPT_TEXT_COLOR : 'rgba(248, 243, 236, 0.82)',
                                                        backgroundColor: isActive ? 'rgba(248, 243, 236, 0.18)' : 'transparent',
                                                        border: isActive ? '1px solid rgba(248, 243, 236, 0.3)' : '1px solid transparent',
                                                        boxShadow: isActive ? '0 4px 14px rgba(10, 6, 2, 0.25)' : 'none',
                                                        '&:hover': {
                                                            backgroundColor: isActive ? 'rgba(248, 243, 236, 0.24)' : 'rgba(248, 243, 236, 0.10)',
                                                            borderColor: 'rgba(248, 243, 236, 0.22)',
                                                        },
                                                    }}
                                                >
                                                    {item.label}
                                                </Button>
                                            )
                                        })}
                                    </Box>
                                    <Box sx={{ flexGrow: 1 }} />
                                    <LanguageSelector
                                        languages={[LanguageCode.CS, LanguageCode.SK, LanguageCode.EN]}
                                        selectedLanguage={selectedLanguage}
                                        onLanguageChange={(lang) => {
                                            setSelectedLanguage(lang)
                                            dispatch(setLanguage(lang))
                                        }}
                                    />
                                    <IconButton
                                        color="inherit"
                                        onClick={() => {
                                            dispatch(logout())
                                            navigate('/')
                                        }}
                                        sx={{
                                            minHeight: 44,
                                            minWidth: 44,
                                            borderRadius: 2,
                                        }}
                                    >
                                        <LogoutIcon />
                                    </IconButton>
                                </>
                            )}
                        </Toolbar>
                    </AppBar>
                    <Menu
                        id="mobile-menu"
                        anchorEl={mobileMenuAnchorEl}
                        open={openMobileMenu}
                        onClose={() => setMobileMenuAnchorEl(null)}
                        MenuListProps={{ 'aria-labelledby': 'mobile-menu-button' }}
                    >
                        {mobileMenuItems.map(item => (
                            <MenuItem
                                key={item.label}
                                selected={activeNavItem === item.key}
                                onClick={() => { setMobileMenuAnchorEl(null); item.action(); }}
                                sx={{
                                    borderRadius: 1,
                                    mx: 1,
                                    my: 0.4,
                                    minHeight: 40,
                                    color: activeNavItem === item.key ? '#5E4634' : '#3E3329',
                                }}
                            >
                                {item.label}
                            </MenuItem>
                        ))}
                    </Menu>
                </>
            )}
        </Box>
    )
}
